/**
 * Timeline (Gantt) View — V5.1
 *
 * Horizontal bars on a date axis. Items are positioned by their `date`
 * property; duration is inferred from status (done/archived = past, others
 * = future window) or can be overridden by a `duration` property if present.
 *
 * Zoom levels:
 *   day:    14 days visible, 50px/day
 *   week:   8 weeks visible, 100px/week
 *   month:  6 months visible, 200px/month
 *   quarter: 4 quarters visible, 300px/quarter
 *
 * Bars are clickable → open peek.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Text,
  Group,
  Badge,
  Stack,
  Box,
  Button,
  ActionIcon,
  ScrollArea,
  Tooltip,
  SegmentedControl,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';

interface TimelineViewProps {
  database: Database;
  items: DatabaseItem[];
  datePropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
}

type Zoom = 'day' | 'week' | 'month' | 'quarter';

const ZOOM_PX: Record<Zoom, number> = {
  day: 50, week: 100, month: 200, quarter: 300,
};
const ZOOM_DAYS: Record<Zoom, number> = {
  day: 1, week: 7, month: 30, quarter: 90,
};

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfWeek(d: Date) {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfQuarter(d: Date) {
  const q = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), q, 1);
}

function useResponsiveLayout() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return { isMobile };
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  database,
  items,
  datePropertyId,
  onItemSelect,
  onItemUpdate,
}) => {
  const [zoom, setZoom] = useState<Zoom>('week');
  const [origin, setOrigin] = useState<Date>(() => startOfWeek(new Date()));
  const { isMobile } = useResponsiveLayout();

  // Compute the visible range
  const range = useMemo(() => {
    const start = origin;
    const end = new Date(origin);
    if (zoom === 'day') end.setDate(end.getDate() + 14);
    else if (zoom === 'week') end.setDate(end.getDate() + 7 * 8);
    else if (zoom === 'month') end.setMonth(end.getMonth() + 6);
    else end.setMonth(end.getMonth() + 3);
    return { start, end };
  }, [origin, zoom]);

  // Compute ticks (column headers) for the axis
  const ticks = useMemo(() => {
    const out: { x: number; label: string; isMajor: boolean }[] = [];
    if (zoom === 'day') {
      for (let i = 0; i < 14; i++) {
        const d = new Date(range.start);
        d.setDate(d.getDate() + i);
        out.push({
          x: i * ZOOM_PX.day,
          label: d.getDate().toString(),
          isMajor: d.getDay() === 0,
        });
      }
    } else if (zoom === 'week') {
      for (let i = 0; i < 8; i++) {
        const d = new Date(range.start);
        d.setDate(d.getDate() + i * 7);
        out.push({
          x: i * ZOOM_PX.week,
          label: d.toLocaleString('default', { month: 'short', day: 'numeric' }),
          isMajor: true,
        });
      }
    } else if (zoom === 'month') {
      for (let i = 0; i < 6; i++) {
        const d = new Date(range.start);
        d.setMonth(d.getMonth() + i);
        out.push({
          x: i * ZOOM_PX.month,
          label: d.toLocaleString('default', { month: 'short', year: '2-digit' }),
          isMajor: true,
        });
      }
    } else {
      for (let i = 0; i < 4; i++) {
        const d = new Date(range.start);
        d.setMonth(d.getMonth() + i * 3);
        out.push({
          x: i * ZOOM_PX.quarter,
          label: `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear().toString().slice(2)}`,
          isMajor: true,
        });
      }
    }
    return out;
  }, [range, zoom]);

  const totalWidth = (() => {
    if (zoom === 'day') return 14 * ZOOM_PX.day;
    if (zoom === 'week') return 8 * ZOOM_PX.week;
    if (zoom === 'month') return 6 * ZOOM_PX.month;
    return 4 * ZOOM_PX.quarter;
  })();

  // Position an item on the timeline
  const position = (item: DatabaseItem): { left: number; width: number; dateStr: string } | null => {
    if (!datePropertyId) return null;
    const prop = item.properties.find(p => p.propertyId === datePropertyId);
    if (!prop?.value) return null;
    const dateStr = String(prop.value).split('T')[0];
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    const days = (date.getTime() - range.start.getTime()) / (24 * 60 * 60 * 1000);
    const pxPerDay = ZOOM_PX[zoom] / ZOOM_DAYS[zoom];
    const left = Math.max(0, days * pxPerDay);
    // Width: status-based default (in_progress = 3 days, done = 1 day, backlog = 0.5 day)
    const status = item.properties.find(p => p.propertyId === 'status')?.value as { id?: string } | undefined;
    const sid = status?.id ?? '';
    let widthDays = 3;
    if (sid === 'status_done' || sid === 'status_archived') widthDays = 1;
    else if (sid === 'status_backlog') widthDays = 0.5;
    else if (sid === 'status_review') widthDays = 2;
    const width = Math.max(20, widthDays * pxPerDay);
    return { left, width, dateStr };
  };

  // Today's vertical line
  const todayX = useMemo(() => {
    const today = startOfDay(new Date());
    const days = (today.getTime() - range.start.getTime()) / (24 * 60 * 60 * 1000);
    if (days < 0 || days > 14 && zoom === 'day' || days > 56 && zoom === 'week') return -1;
    const pxPerDay = ZOOM_PX[zoom] / ZOOM_DAYS[zoom];
    return days * pxPerDay;
  }, [range, zoom]);

  // Color from status or priority
  const colorFor = (item: DatabaseItem): string => {
    const status = item.properties.find(p => p.propertyId === 'status')?.value as { color?: string } | undefined;
    if (status?.color) return status.color;
    const prio = item.properties.find(p => p.propertyId === 'priority')?.value as { color?: string } | undefined;
    return prio?.color ?? '#94a3b8';
  };

  const itemsWithDates = items
    .map(item => ({ item, pos: position(item) }))
    .filter((x): x is { item: DatabaseItem; pos: { left: number; width: number; dateStr: string } } => x.pos !== null);

  // Sort by date for stable row layout
  itemsWithDates.sort((a, b) => a.pos.left - b.pos.left);

  // Step origin
  const step = (dir: 1 | -1) => {
    const d = new Date(origin);
    if (zoom === 'day') d.setDate(d.getDate() + dir * 7);
    else if (zoom === 'week') d.setDate(d.getDate() + dir * 7 * 4);
    else if (zoom === 'month') d.setMonth(d.getMonth() + dir * 3);
    else d.setMonth(d.getMonth() + dir * 3);
    setOrigin(d);
  };

  const today = () => setOrigin(startOfWeek(new Date()));

  return (
    <Box style={{ width: '100%', padding: isMobile ? '12px' : '20px' }}>
      <Paper p={isMobile ? 'sm' : 'md'} radius="md" withBorder>
        {/* Header */}
        <Group justify="space-between" align="center" mb="md" wrap="wrap" gap="sm">
          <Group gap="xs">
            <ActionIcon onClick={() => step(-1)} variant="subtle" aria-label="Earlier">
              <IconChevronLeft size={16} />
            </ActionIcon>
            <ActionIcon onClick={() => step(1)} variant="subtle" aria-label="Later">
              <IconChevronRight size={16} />
            </ActionIcon>
            <Button size="xs" variant="light" onClick={today}>Today</Button>
          </Group>
          <SegmentedControl
            value={zoom}
            onChange={(v) => setZoom(v as Zoom)}
            data={[
              { label: 'Day',     value: 'day' },
              { label: 'Week',    value: 'week' },
              { label: 'Month',   value: 'month' },
              { label: 'Quarter', value: 'quarter' },
            ]}
            size="xs"
          />
          <Badge variant="light">{itemsWithDates.length} items</Badge>
        </Group>

        {/* Timeline body */}
        {itemsWithDates.length === 0 ? (
          <Box py="xl" style={{ textAlign: 'center' }}>
            <Text c="dimmed" size="sm">No items with a date to display</Text>
          </Box>
        ) : (
          <ScrollArea>
            <Box style={{ position: 'relative', minWidth: totalWidth, paddingBottom: 8 }}>
              {/* Axis ticks */}
              <Box style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                height: 32,
                background: 'var(--mantine-color-body)',
                borderBottom: '1px solid var(--mantine-color-gray-3)',
              }}>
                {ticks.map(t => (
                  <Box
                    key={`tick-${t.x}`}
                    style={{
                      position: 'absolute',
                      left: t.x,
                      top: 0,
                      bottom: 0,
                      borderLeft: t.isMajor ? '1px solid var(--mantine-color-gray-3)' : '1px dashed var(--mantine-color-gray-2)',
                      paddingLeft: 4,
                      fontSize: 11,
                      color: t.isMajor ? 'var(--mantine-color-text)' : 'var(--mantine-color-dimmed)',
                    }}
                  >
                    <Text size="xs" fw={t.isMajor ? 600 : 400} style={{ paddingTop: 6 }}>{t.label}</Text>
                  </Box>
                ))}
              </Box>

              {/* Today's line */}
              {todayX >= 0 && todayX <= totalWidth && (
                <Box
                  style={{
                    position: 'absolute',
                    left: todayX,
                    top: 32,
                    bottom: 0,
                    width: 2,
                    background: 'var(--mantine-color-red-filled, #ef4444)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Item rows */}
              <Stack gap={4} mt="xs">
                {itemsWithDates.map(({ item, pos }) => (
                  <Tooltip key={item.id} label={`${item.title} — ${pos.dateStr}`} withArrow>
                    <Box
                      onClick={() => onItemSelect?.(item)}
                      style={{
                        position: 'relative',
                        height: 32,
                        cursor: 'pointer',
                      }}
                    >
                      {/* Label gutter (left, sticky) */}
                      <Box
                        style={{
                          position: 'absolute',
                          left: -160,
                          top: 0,
                          width: 152,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          paddingRight: 8,
                          fontSize: 12,
                          color: 'var(--mantine-color-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.title}
                      </Box>
                      {/* The bar */}
                      <Box
                        style={{
                          position: 'absolute',
                          left: pos.left,
                          width: pos.width,
                          height: 24,
                          top: 4,
                          backgroundColor: colorFor(item),
                          borderRadius: 4,
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 500,
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                          transition: 'transform 120ms ease, box-shadow 120ms ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.12)';
                        }}
                      >
                        {item.title}
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </ScrollArea>
        )}

        <Text size="xs" c="dimmed" mt="sm">
          Timeline view — bars positioned by the item's date. Hover for full title. Tap to open.
        </Text>
      </Paper>
    </Box>
  );
};

export default TimelineView;
